import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ALL_DAYS, sessionKey, copyEntry, anchorIdsOf } from '../../services/anchors';
import { Check, Search, Briefcase, Clock, Shield, ShieldOff, Anchor, Lock, Plus, AlertTriangle } from 'lucide-react';
import Drawer from '../ui/Drawer';
import Button from '../ui/Button';
import SegmentedControl from '../ui/SegmentedControl';
import { INSTITUTIONS } from '../../services/staffFields';
import Switch from '../ui/Switch';
import { apiFetch } from '../../services/apiClient';
import PatternGrid from '../ui/PatternGrid';
import {
  parseCellKey, pick, computePatternWarnings, cellsToPayload,
  payloadToCells,
} from '../../services/patternCells';

// Day index === the stored `day` value, aligned with Python's weekday():
// Mon=0 .. Fri=4, Sat=5, Sun=6. Module scope so the pattern helpers defined at
// the top of LocationDrawer can use it without hitting the temporal dead zone.
const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Key for a single pattern cell. Shift-type ids may contain underscores, so the
// day is split off from the FRONT only -- never use a naive split('_').

// --- SUB-COMPONENT: Searchable Selection List ---
const SearchableList = ({ label, items, selected, onToggle, icon: Icon, placeholder, height = "h-48" }) => {
  const [search, setSearch] = useState('');
  
  const filtered = useMemo(() => items.filter(i => i.toLowerCase().includes(search.toLowerCase())), [items, search]);
  
  const sortedItems = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aSel = selected.includes(a);
      const bSel = selected.includes(b);
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return a.localeCompare(b);
    });
  }, [filtered, selected]);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Icon className="w-3 h-3 text-slate-400" />
          <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em]">{label}</label>
        </div>
        <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{selected.length} Selected</span>
      </div>
      <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm focus-within:ring-2 focus-within:ring-[var(--primary)]/20 focus-within:border-[var(--primary)] transition-all">
        <div className="flex items-center px-3 py-2 border-b border-slate-100 bg-slate-50/50">
          <Search className="w-3.5 h-3.5 text-slate-400 mr-2" />
          <input 
            type="text" 
            value={search} 
            onChange={(e) => setSearch(e.target.value)} 
            placeholder={placeholder} 
            className="bg-transparent border-none outline-none text-xs w-full font-medium text-slate-600 placeholder:text-slate-300" 
          />
        </div>
        <div className={`${height} overflow-y-auto custom-scrollbar p-1.5 space-y-0.5`}>
          {sortedItems.length > 0 ? sortedItems.map(item => {
            const isSelected = selected.includes(item);
            return (
              <button 
                key={item} 
                onClick={() => onToggle(item)} 
                className={`w-full text-left px-3 py-2 rounded-lg text-[11px] font-bold transition-all flex items-center justify-between group ${isSelected ? 'bg-[#042f2e] text-white shadow-md' : 'hover:bg-slate-50 text-slate-600'}`}
              >
                <span>{item.replace(/^skill_/i, '')}</span>
                {isSelected ? <Check className="w-3 h-3" /> : <Plus className="w-3 h-3 opacity-0 group-hover:opacity-100 text-slate-300" />}
              </button>
            );
          }) : <div className="py-8 text-center text-[10px] text-slate-300 font-bold uppercase tracking-widest">No matches</div>}
        </div>
      </div>
    </div>
  );
};

const LocationDrawer = ({ 
  isOpen, 
  onClose, 
  location, 
  allSkills, 
  employees, 
  preferences, 

  isActive, 
  getEmployeeColor,
  onUpdateSettings,
  onToggleActive,
  onUpdateAnchors,
  locationType = '',   // 'OT' | 'Clinic' | '' (not set)
  locationSite = '',   // 'NUH' | 'NTFGH' | 'AH' | 'JMC' | '' (not set)
  anchorRoles = {},    // staff id -> 'lead' | 'support'; absent = either
}) => {
  const [activeTab, setActiveTab] = useState('settings');
  const [localName, setLocalName] = useState('');
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [localType, setLocalType] = useState('');
  const [localSite, setLocalSite] = useState('');
  const [localPrefs, setLocalPrefs] = useState({});
  const [localRoles, setLocalRoles] = useState({});
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);   // 'saved' | null — inline confirm, drawer stays open
  const lastLocationRef = useRef(null);

  // --- shift pattern + staffing ------------------------------------------
  // Replaces the old "Hours" tab. That tab wrote location_schedules via
  // /api/locations/{name}/schedule, but generation is now driven entirely by
  // location_shift_patterns: set_full_location_schedule() only writes the
  // legacy field, and the legacy->pattern derivation is one-time
  // (`if location in patterns: continue`), so hours edits were a silent no-op.
  const [shiftTypes, setShiftTypes] = useState([]);
  const [rankLabels, setRankLabels] = useState({});   // clinical rank number -> name, for the ceiling dropdown
  const [patternCells, setPatternCells] = useState({});   // key -> {lead:{min,target,maxRank}, support:{min,target}}
  const [patternLoading, setPatternLoading] = useState(false);
  const [patternError, setPatternError] = useState(null);
  // Coverage policy: "open" (default) always rosters when staffed; "anchor_gated"
  // closes the location's shift when its anchored staff are unavailable.
  const [coveragePolicy, setCoveragePolicy] = useState('open');

  // Initialize state on open
  useEffect(() => {
    if (isOpen && location) {
      setLocalName(location.name || '');
      setSelectedSkills(location.currentSkills || []);
      setLocalType(locationType || '');
      setLocalSite(locationSite || '');
      
      // A copy of this location's ties (staff ids) for local editing.
      setLocalPrefs(copyEntry(preferences));
      setLocalRoles({ ...(anchorRoles || {}) });
      
      // Only reset active tab if the location has changed since we last opened/refreshed
      if (lastLocationRef.current !== location.id) {
        setActiveTab('settings');
        lastLocationRef.current = location.id;
      }
    } else if (!isOpen) {
      lastLocationRef.current = null;
    }
    // Keyed on the roles' CONTENT: the caller builds a fresh {} on every render
    // when a clinic has none, and a reference dep would reset every edit here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, location, preferences, locationType, locationSite, JSON.stringify(anchorRoles || {})]);

  // Load active shift types plus this location's pattern and staffing rows.
  useEffect(() => {
    // Clinical rank display names for the lead seniority-ceiling dropdown.
    apiFetch('/api/rank-maps')
      .then(r => r.json())
      .then(d => setRankLabels(d.clinicalRankLabels || d.clinical_rank_labels || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isOpen || !location?.id) return;
    let cancelled = false;

    (async () => {
      setPatternLoading(true);
      setPatternError(null);
      const enc = encodeURIComponent(location.id);
      try {
        const [typesRes, patternRes, staffingRes, policyRes] = await Promise.all([
          apiFetch('/api/shift-types'),
          apiFetch(`/api/locations/${enc}/shift-pattern`),
          apiFetch(`/api/locations/${enc}/staffing-requirements`),
          apiFetch('/api/locations/coverage-policies'),
        ]);
        const [types, patternBody, staffingBody, policyBody] = await Promise.all([
          typesRes.json(), patternRes.json(), staffingRes.json(), policyRes.json(),
        ]);
        if (cancelled) return;

        const policies = policyBody?.policies || {};
        const stored = policies[location.id];
        setCoveragePolicy(['anchor_gated', 'anchor_gated_lead'].includes(stored) ? stored : 'open');

        setShiftTypes((Array.isArray(types) ? types : [])
          .filter(t => pick(t, 'isActive', 'is_active') !== false));

        // Same hydration the creation modal uses, so both screens read a
        // stored pattern identically -- this was a hand-rolled second copy.
        const cells = payloadToCells(patternBody?.pattern || [],
                                     staffingBody?.requirements || []);
        setPatternCells(cells);
      } catch {
        if (!cancelled) setPatternError('Could not load shift pattern.');
      } finally {
        if (!cancelled) setPatternLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [isOpen, location?.id]);

  // The cell model lives in services/patternCells so the creation modal and this
  // drawer cannot drift apart. These wrappers only add the "you have unsaved
  // changes" bookkeeping, which is this screen's concern alone.
  const applyCells = (next) => {
    setPatternCells(next);
    setSaveStatus(null);
  };

  const patternWarnings = useMemo(
    () => computePatternWarnings(patternCells, shiftTypes),
    [patternCells, shiftTypes]);

  const savePattern = async (locationId) => {
    const enc = encodeURIComponent(locationId);
    const { pattern, requirements } = cellsToPayload(patternCells);

    // ORDER MATTERS. Writing the pattern triggers
    // _reconcile_staffing_requirements_for_location(), which defaults every
    // target to 1 -- the pattern's own required_staff does NOT propagate.
    // Staffing must therefore be written SECOND or the real numbers are lost.
    const patternRes = await apiFetch(`/api/locations/${enc}/shift-pattern`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ pattern }),
    });
    const patternBody = await patternRes.json().catch(() => null);
    if (!patternRes.ok || patternBody?.success === false) {
      return { ok: false, message: (patternBody?.problems || []).join('; ')
        || patternBody?.message || 'Failed to save shift pattern.' };
    }

    const staffingRes = await apiFetch(`/api/locations/${enc}/staffing-requirements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ requirements }),
    });
    const staffingBody = await staffingRes.json().catch(() => null);
    if (!staffingRes.ok || staffingBody?.success === false) {
      return { ok: false, message: (staffingBody?.problems || []).join('; ')
        || staffingBody?.message || 'Failed to save staffing requirements.' };
    }

    return { ok: true, gaps: patternBody?.staffing_gaps || [] };
  };

  const anchoredIds = useMemo(() => anchorIdsOf(localPrefs), [localPrefs]);

  // Live: reflects Ties-tab edits immediately, so the anchor_gated warning
  // clears the moment an anchor is added.
  const hasAnchoredStaff = anchoredIds.size > 0;

  const sortedEmployees = useMemo(() => {
    return [...employees].sort((a, b) => {
      const aAnc = anchoredIds.has(a.id);
      const bAnc = anchoredIds.has(b.id);
      if (aAnc && !bAnc) return -1;
      if (!aAnc && bAnc) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [employees, anchoredIds]);

  useEffect(() => {
    if (activeTab === 'ties' && !selectedEmp && sortedEmployees.length > 0) {
      const firstAnchored = sortedEmployees.find(e => anchoredIds.has(e.id));
      setSelectedEmp(firstAnchored || sortedEmployees[0]);
    }
  }, [activeTab, sortedEmployees, anchoredIds, selectedEmp]);

  const handleMasterSave = async () => {
    setIsProcessing(true);
    
    const typeChanged = (localType || '') !== (locationType || '');
    const siteChanged = (localSite || '') !== (locationSite || '');
    const settingsChanged = localName !== location.name || JSON.stringify(selectedSkills) !== JSON.stringify(location.currentSkills) || typeChanged || siteChanged;
    if (settingsChanged && localName.trim()) {
      await onUpdateSettings(location.id, localName, selectedSkills, typeChanged ? localType : undefined,
                             siteChanged ? localSite : undefined);
    }

    // Shift pattern + staffing, addressed by the clinic's id (a rename changes
    // nothing they refer to).
    const targetName = location.id;

    // Coverage policy — cheap idempotent write; failure here should not block the
    // roster save, so it is fire-and-forget with a console warning only.
    try {
      await apiFetch(`/api/locations/${encodeURIComponent(targetName)}/coverage-policy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ policy: coveragePolicy }),
      });
    } catch (e) {
      console.warn('Coverage policy save failed:', e);
    }

    const result = await savePattern(targetName);
    if (!result.ok) {
      setPatternError(result.message);
      setActiveTab('pattern');
      setIsProcessing(false);
      return;   // keep the drawer open so the error is visible
    }

    await onUpdateAnchors({
        location: location.id,
        full_prefs: localPrefs,
        roles: localRoles,
    });

    setIsProcessing(false);
    // Persist and STAY OPEN so configuration can continue. A transient
    // confirmation replaces the previous auto-close.
    setSaveStatus('saved');
    setTimeout(() => setSaveStatus(null), 2500);
  };

  const handleToggleAnchorLocal = (staffId, dayIdx = null, timeBlock = null) => {
    const key = sessionKey(dayIdx, timeBlock);
    if (!key || !staffId) return;
    const currentList = localPrefs[key] || [];
    const isCurrentlyActive = currentList.includes(staffId);

    const newList = isCurrentlyActive
      ? currentList.filter(id => id !== staffId)
      : [...currentList, staffId];

    setLocalPrefs({
      ...localPrefs,
      [key]: newList
    });
  };


  const blocks = ["AM", "PM"];
  // idx === stored day number, aligned with weekday(): Sat=5, Sun=6.
  // The Shifts tab always offers the weekend (opt-in); Ties only shows weekend
  // rows once this location actually operates then. Derived from the PATTERN --
  // location_schedules no longer drives anything, so reading it here would make
  // the Ties grid disagree with what is actually rostered.
  const schedHasWeekend = useMemo(
    () => Object.keys(patternCells).some(k => parseCellKey(k).day >= 5),
    [patternCells]
  );

  const renderSettings = () => (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Location Name</label>
        <input 
          type="text" 
          value={localName} 
          onChange={(e) => setLocalName(e.target.value)} 
          placeholder="Location Name" 
          className="w-full px-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-[var(--primary)]/20 outline-none transition-all text-xs font-bold text-slate-700 shadow-sm h-[36px]" 
        />
      </div>
      {/* Optional OT / Clinic tag, used by rules that treat theatres differently. */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Location type</label>
        <SegmentedControl value={localType} onChange={setLocalType}
          options={[{ id: '', label: 'Not set' }, { id: 'Clinic', label: 'Clinic' }, { id: 'OT', label: 'OT (theatre)' }]}
          className="h-[36px]" />
      </div>
      {/* Which institution the location sits at. Rules that keep people at one
          site, and the Final Roster / Excel sections, read this -- not the name. */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.15em] ml-1">Site</label>
        <SegmentedControl value={localSite} onChange={setLocalSite}
          options={[{ id: '', label: 'Not set' }, ...INSTITUTIONS.map(i => ({ id: i, label: i }))]}
          className="h-[36px]" />
      </div>
      <SearchableList 
        label="Required Qualifications" 
        items={allSkills} 
        selected={selectedSkills} 
        onToggle={(skill) => {
          if (selectedSkills.includes(skill)) setSelectedSkills(selectedSkills.filter(i => i !== skill));
          else setSelectedSkills([...selectedSkills, skill]);
        }} 
        icon={Briefcase} 
        placeholder="Search library..." 
        height="h-64"
      />
    </div>
  );

  const renderPattern = () => {
    const active = Object.keys(patternCells).length;

    return (
      <div className="space-y-4 animate-in fade-in duration-300">
        <div className="p-1 bg-slate-100 rounded-2xl border border-slate-200">
          <div className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${isActive ? 'bg-white shadow-md' : 'opacity-60 grayscale-[0.5]'}`}>
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center border-2 ${isActive ? 'bg-emerald-50 border-emerald-500 text-emerald-600' : 'bg-slate-100 border-slate-300 text-slate-400'}`}>
              {isActive ? <Shield className="w-5 h-5" /> : <ShieldOff className="w-5 h-5" />}
            </div>
            <div className="text-left flex-1">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-1">Operating Status</p>
              <p className={`text-xs font-bold ${isActive ? 'text-emerald-700' : 'text-slate-500'}`}>{isActive ? 'Operating' : 'Inactive'}</p>
            </div>
            <Switch isOn={isActive} onClick={() => onToggleActive(location.id)} activeTrack="bg-emerald-500" inactiveTrack="bg-slate-400" />
          </div>

          <div className={`mt-2 p-3 rounded-xl bg-white transition-all ${!isActive ? 'opacity-60' : ''}`}>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest leading-none mb-2">Coverage Policy</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {[
                { id: 'open', title: 'Open', desc: 'Always roster when staffed; solver fills from all eligible staff.' },
                { id: 'anchor_gated', title: 'Anchor-gated', desc: 'Close the shift when the anchored staff are unavailable (single-doctor clinic).' },
                { id: 'anchor_gated_lead', title: 'Lead-gated', desc: 'When the anchored staff are unavailable, keep the session open for support staff but leave the lead for you to fill (theatre).' },
              ].map(opt => {
                const on = coveragePolicy === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setCoveragePolicy(opt.id)}
                    className={`text-left p-2.5 rounded-lg border-2 transition-all ${on ? 'bg-emerald-50 border-emerald-500' : 'bg-white border-slate-100 hover:border-slate-300'}`}
                  >
                    <p className={`text-[11px] font-bold mb-0.5 ${on ? 'text-emerald-700' : 'text-slate-600'}`}>{opt.title}</p>
                    <p className="text-[9px] font-medium text-slate-400 leading-tight">{opt.desc}</p>
                  </button>
                );
              })}
            </div>
            {coveragePolicy !== 'open' && !hasAnchoredStaff && (
              <div className="flex items-start gap-1.5 mt-2 text-[9px] font-bold text-amber-600">
                <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
                <span>No anchored staff set for this location — it behaves like Open until a location tie is added in the Ties tab.</span>
              </div>
            )}
          </div>
        </div>

        {patternError && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-rose-200 bg-rose-50">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-500 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] font-bold text-rose-700 leading-relaxed">{patternError}</p>
          </div>
        )}

        {patternWarnings.length > 0 && (
          <div className="flex items-start gap-2 p-3 rounded-xl border border-amber-200 bg-amber-50">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
            <div className="text-[10px] font-bold text-amber-700 leading-relaxed space-y-0.5">
              {patternWarnings.map(w => <p key={w}>{w}</p>)}
            </div>
          </div>
        )}

        <div className={`space-y-2 transition-all ${!isActive ? 'opacity-30 pointer-events-none' : ''}`}>
          <div className="flex items-center justify-between mb-1 ml-1">
            <div className="flex items-center gap-2">
              <Clock className="w-3 h-3 text-slate-400" />
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Shift Pattern</p>
            </div>
            <span className="text-[9px] font-bold text-slate-300 uppercase tracking-widest">{active} active</span>
          </div>

          {patternLoading ? (
            <div className="py-10 text-center text-[10px] font-bold text-slate-300 uppercase tracking-widest">Loading pattern…</div>
          ) : (
            <PatternGrid
              shiftTypes={shiftTypes}
              cells={patternCells}
              onChange={applyCells}
              rankLabels={rankLabels}
            />
          )}
        </div>
      </div>
    );
  };

  const renderTies = () => (
    <div className="flex h-full animate-in fade-in duration-300 gap-4">
      {/* Staff Sidebar */}
      <div className="w-[160px] flex flex-col gap-2">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1 mb-1">Staff List</p>
        <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-1">
          {sortedEmployees.map(emp => {
            const isGlobal = (localPrefs[ALL_DAYS] || []).includes(emp.id);
            const hasGranular = Object.keys(localPrefs).some(k => k !== ALL_DAYS && (localPrefs[k] || []).includes(emp.id));
            const isSelected = selectedEmp?.id === emp.id;
            const isAnyAnc = isGlobal || hasGranular;
            
            return (
              <button 
                key={emp.id || emp.name} 
                onClick={() => setSelectedEmp(emp)}
                className={`w-full text-left px-2 py-1.5 rounded-xl transition-all flex items-center justify-between border group 
                  ${isAnyAnc ? 'bg-[#042f2e] text-white shadow-md' : 'hover:bg-slate-50 text-slate-600 border-transparent'} 
                  ${isSelected ? (isAnyAnc ? 'ring-1 ring-inset ring-white/30 shadow-inner' : 'bg-slate-100 border-slate-200') : 'border-transparent'}
                `}
              >
                <div className="flex items-center gap-2 truncate">
                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getEmployeeColor(emp.name) }} />
                  <span className={`text-[10px] font-bold truncate ${isAnyAnc ? 'text-white' : (isSelected ? 'text-[var(--primary)]' : 'text-slate-500')}`}>
                    {emp.name}
                  </span>
                </div>
                <div className="flex gap-0.5 flex-shrink-0">
                  {isGlobal && <Lock className={`w-2.5 h-2.5 ${isAnyAnc ? 'text-white/80' : 'text-[var(--terracotta)]'}`} />}
                  {hasGranular && <Clock className={`w-2.5 h-2.5 ${isAnyAnc ? 'text-white/80' : 'text-teal-500'}`} />}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Configuration */}
      <div className="flex-1 flex flex-col gap-4">
        {selectedEmp ? (
          <>
            <div className={`p-3 rounded-xl border transition-all shadow-sm
              ${(localPrefs[ALL_DAYS] || []).includes(selectedEmp.id) ? 'bg-[#042f2e] text-white border-transparent' : 'bg-slate-50 border-slate-200 text-slate-600'}
            `}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className={`p-1.5 rounded-lg shadow-xs border transition-colors
                    ${(localPrefs[ALL_DAYS] || []).includes(selectedEmp.id) ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-slate-200 text-slate-400'}
                  `}>
                    <Lock className="w-3.5 h-3.5" />
                  </div>
                  <div>
                    <h4 className={`text-[11px] font-bold leading-tight ${(localPrefs[ALL_DAYS] || []).includes(selectedEmp.id) ? 'text-white' : 'text-slate-800'}`}>Global Tie</h4>
                    <p className={`text-[9px] font-bold uppercase tracking-wider mt-0.5 ${(localPrefs[ALL_DAYS] || []).includes(selectedEmp.id) ? 'text-white/60' : 'text-slate-400'}`}>Fixed allocation for all shifts</p>
                  </div>
                </div>
                <Switch 
                  isOn={(localPrefs[ALL_DAYS] || []).includes(selectedEmp.id)} 
                  onClick={() => handleToggleAnchorLocal(selectedEmp.id)}
                  inactiveTrack="bg-slate-400"
                  activeTrack="bg-slate-400"
                />
              </div>
            </div>

            {/* Which slots this anchor is tied to. Only meaningful once the
                person anchors this location, so shown only then. */}
            {Object.values(localPrefs).some(list => (list || []).includes(selectedEmp.id)) && (
              <div className="flex items-center justify-between gap-3 px-1">
                <div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Anchors the</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">
                    {(localRoles[selectedEmp.id] || 'either') === 'lead' ? 'Lead slots only — and may lead here'
                      : (localRoles[selectedEmp.id] || 'either') === 'support' ? 'Support slots only'
                      : 'Lead or support slots'}
                  </p>
                </div>
                <div className="w-[210px]">
                  <SegmentedControl
                    value={localRoles[selectedEmp.id] || 'either'}
                    onChange={(role) => setLocalRoles(prev => ({ ...prev, [selectedEmp.id]: role }))}
                    options={[{ id: 'lead', label: 'Lead' }, { id: 'support', label: 'Support' }, { id: 'either', label: 'Either' }]}
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center gap-2 ml-1">
                <Clock className="w-3 h-3 text-slate-400" />
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Shift-Specific Ties</p>
              </div>
              <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 shadow-inner space-y-2">
                <div className="flex gap-2 mb-0.5">
                  <div className="w-10"></div>
                  {blocks.map(b => (
                    <div key={b} className="flex-1 text-center text-[9px] font-bold text-slate-300 uppercase tracking-[0.2em]">{b}</div>
                  ))}
                </div>
                {(schedHasWeekend ? days : days.slice(0, 5)).map((day, idx) => (
                  <React.Fragment key={day}>
                    {idx === 5 && (
                      <div className="flex items-center gap-2 pt-1.5 pb-0.5">
                        <div className="w-10" />
                        <div className="flex-1 text-[8px] font-bold text-slate-300 uppercase tracking-[0.2em]">Weekend</div>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="w-10 text-[9px] font-bold text-slate-400 uppercase tracking-wider">{day.slice(0, 3)}</div>
                      {blocks.map(block => {
                        const active = (localPrefs[sessionKey(idx, block)] || []).includes(selectedEmp.id);
                        return (
                          <button 
                            key={block} 
                            onClick={() => handleToggleAnchorLocal(selectedEmp.id, idx, block)}
                            className={`flex-1 h-7 rounded-xl border-2 transition-all flex items-center justify-center ${active ? 'bg-[#042f2e] border-transparent text-white shadow-sm' : 'bg-white border-slate-100 text-transparent hover:border-slate-300'}`}
                          >
                            <Check className={`w-3 h-3 font-bold ${active ? 'opacity-100' : 'opacity-0'}`} />
                          </button>
                        );
                      })}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center opacity-30 text-center">
            <Anchor className="w-8 h-8 mb-2 text-slate-400" />
            <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">Select staff<br/>to begin tying</p>
          </div>
        )}
      </div>
    </div>
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      {saveStatus === 'saved' && (
        <span className="flex items-center gap-1.5 text-[11px] font-bold text-emerald-600 mr-auto animate-in fade-in">
          <Check className="w-3.5 h-3.5" /> Saved — keep configuring or close when done
        </span>
      )}
      <Button variant="secondary" onClick={onClose} className="px-6 h-[36px] text-xs font-bold">Close</Button>
      <Button 
        variant="primary" 
        onClick={handleMasterSave} 
        isLoading={isProcessing} 
        className="px-8 h-[36px] text-xs font-bold"
        icon={Check}
      >
        Save Changes
      </Button>
    </div>
  );

  return (
    <Drawer 
      isOpen={isOpen} 
      onClose={onClose} 
      title={location?.name || 'Location Management'}
      footer={footer}
    >
      <div className="flex flex-col h-full">
        <SegmentedControl 
          className="mb-4"
          options={[
            { id: 'settings', label: 'Settings' },
            { id: 'pattern', label: 'Shifts' },
            { id: 'ties', label: 'Location Ties' }
          ]}
          value={activeTab}
          onChange={setActiveTab}
        />
        
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'pattern' && renderPattern()}
          {activeTab === 'ties' && renderTies()}
        </div>
      </div>
    </Drawer>
  );
};

export default LocationDrawer;
